/**
 * Booxi Service Filters - client-side filtering for Booxi service cards.
 * Filters appear only when collection's service_tag matches Booxi services.
 * Real-time filtering: updates URL and filters immediately on input change (no Apply needed).
 */

/** @type {Record<string, string>} */
const BOOXI_PARAMS = {
  price: 'filter_service_price',
  brand: 'filter_service_brand',
  duration: 'filter_service_duration',
  category: 'filter_service_category',
};

const BOOXI_FILTER_INPUT_SELECTOR =
  'input[name="filter_service_price"], input[name="filter_service_brand"], input[name="filter_service_duration"], input[name="filter_service_category"]';

/** Labels for Booxi param values */
const BOOXI_LABELS = {
  price: { complimentary: 'Complimentary', paid: 'Paid' },
  duration: { '5-15': '5-15 min', '20-45': '20-45 min', '45+': '45 Min and above' },
};

/**
 * @typedef {{ price: string[], brand: string[], duration: string[], category: string[] }} BooxiParams
 */

/** Handleized brand slug for “Dermalogica” (matches Liquid `brand | handleize` on facet inputs). */
const BOOKING_CTA_BRAND_HANDLE = 'dermalogica';

/**
 * True when storefront collection facet params are present (`filter.*`, same notion as facets.js).
 * @returns {boolean}
 */
function hasStandardShopifyFiltersFromURL() {
  const url = new URL(window.location.href);
  return [...url.searchParams.keys()].some((key) => key.startsWith('filter.'));
}

/**
 * Whether any Booxi facet dimensions are active in `params`.
 * @param {BooxiParams} params
 * @returns {boolean}
 */
function hasAnyBooxiFilters(params) {
  return !!(
    params.price.length ||
    params.brand.length ||
    params.duration.length ||
    params.category.length
  );
}

/**
 * Booking CTA: visible by default (no filters). Hidden whenever any filter is applied,
 * except remain visible when the Dermalogica brand Booxi facet is selected.
 * @param {BooxiParams} params
 * @returns {boolean}
 */
function isBookingCtaVisibleFromParams(params) {
  const hasBooxi = hasAnyBooxiFilters(params);
  const hasStandard = hasStandardShopifyFiltersFromURL();

  if (!hasBooxi && !hasStandard) {
    return true;
  }

  const brands = params.brand || [];
  return brands.some((b) => String(b).trim().toLowerCase() === BOOKING_CTA_BRAND_HANDLE);
}

/**
 * Show/hide `.product-grid__item--cta-booking`.
 * @param {Document|HTMLElement} scope
 * @param {BooxiParams} params
 */
function applyBookingCtaVisibility(scope, params) {
  const nodes = scope.querySelectorAll('.product-grid__item--cta-booking');
  if (!nodes.length) return;
  const show = isBookingCtaVisibleFromParams(params);
  nodes.forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.display = show ? '' : 'none';
    }
  });
}

/**
 * Normalize Booxi category values so "...tag" suffix variants map to one value.
 * @param {string} value
 * @returns {string}
 */
function normalizeBooxiCategoryValue(value) {
  const normalizedValue = String(value || '').trim().toLowerCase();
  if (normalizedValue.endsWith('tag')) return normalizedValue.slice(0, -3);
  return normalizedValue;
}

/**
 * Map Booxi input names to params keys.
 * @param {string} name
 * @returns {keyof BooxiParams|''}
 */
function getBooxiParamsKeyFromInputName(name) {
  if (name === 'filter_service_price') return 'price';
  if (name === 'filter_service_brand') return 'brand';
  if (name === 'filter_service_duration') return 'duration';
  if (name === 'filter_service_category') return 'category';
  return '';
}

/**
 * Clone Booxi params to avoid mutating source objects.
 * @param {BooxiParams} params
 * @returns {BooxiParams}
 */
function cloneBooxiParams(params) {
  return {
    price: [...(params.price || [])],
    brand: [...(params.brand || [])],
    duration: [...(params.duration || [])],
    category: [...(params.category || [])],
  };
}

/**
 * When booking CTA mode is on and Dermalogica brand is active, Booxi price/duration/category
 * must be cleared — only brand facets apply (per UX: isolating the Dermalogica booking path).
 * @param {BooxiParams} params
 * @param {boolean} bookingCtaEnabled
 * @returns {{ params: BooxiParams, changed: boolean }}
 */
function normalizeParamsWhenDermalogicaBooking(params, bookingCtaEnabled) {
  if (!bookingCtaEnabled) return { params, changed: false };
  const brands = params.brand || [];
  const hasDermalogica = brands.some((b) => String(b).trim().toLowerCase() === BOOKING_CTA_BRAND_HANDLE);
  if (!hasDermalogica) return { params, changed: false };
  if (!params.price.length && !params.duration.length && !params.category.length) {
    return { params, changed: false };
  }
  return {
    params: {
      ...cloneBooxiParams(params),
      price: [],
      duration: [],
      category: [],
    },
    changed: true,
  };
}

/**
 * @param {BooxiParams} params
 * @returns {boolean}
 */
function isDermalogicaBrandActiveInParams(params) {
  return (params.brand || []).some((b) => String(b).trim().toLowerCase() === BOOKING_CTA_BRAND_HANDLE);
}

/**
 * Normalize input values based on params key.
 * @param {string} value
 * @param {keyof BooxiParams} paramsKey
 * @returns {string}
 */
function normalizeBooxiInputValue(value, paramsKey) {
  return paramsKey === 'category' ? normalizeBooxiCategoryValue(value) : value;
}

/**
 * Build hypothetical params for checking whether an option would have matches.
 * @param {BooxiParams} currentParams
 * @param {HTMLInputElement} input
 * @returns {BooxiParams|null}
 */
function buildHypotheticalParamsForInput(currentParams, input) {
  const paramsKey = getBooxiParamsKeyFromInputName(input.name);
  if (!paramsKey) return null;

  const rawValue = input.value?.trim();
  if (!rawValue) return null;

  const normalizedValue = normalizeBooxiInputValue(rawValue, paramsKey);
  const nextParams = cloneBooxiParams(currentParams);
  const currentValues = nextParams[paramsKey] || [];

  if (input.type === 'radio') {
    nextParams[paramsKey] = [normalizedValue];
    return nextParams;
  }

  if (!currentValues.includes(normalizedValue)) {
    nextParams[paramsKey] = [...currentValues, normalizedValue];
  }

  return nextParams;
}

/**
 * Determine if a service card matches a set of Booxi params.
 * Mirrors the filtering rules used for card visibility.
 * @param {HTMLElement} card
 * @param {BooxiParams} params
 * @returns {boolean}
 */
function booxiCardMatchesParams(card, params) {
  const priceList = params.price || [];
  const brandList = params.brand || [];
  const durationList = params.duration || [];
  const categoryList = params.category || [];

  const priceType = card.dataset.priceType || '';
  const brand = card.dataset.brand || '';
  const durationRange = card.dataset.durationRange || '';
  const serviceTag = normalizeBooxiCategoryValue(card.dataset.serviceTag || '');

  let show = true;

  if (priceList.length && priceType) {
    show = show && priceList.includes(priceType);
  }
  if (brandList.length && brand) {
    show = show && brandList.includes(brand);
  }
  if (durationList.length && durationRange) {
    const cardRanges = durationRange.split(/[\s,]+/).filter(Boolean);
    show = show && cardRanges.length > 0 && cardRanges.some((r) => durationList.includes(r));
  }
  if (categoryList.length) {
    if (!serviceTag) {
      show = false;
    } else {
      show = show && categoryList.includes(serviceTag);
    }
  }

  return show;
}

/**
 * Get current Booxi filter values from URL.
 * @returns {{ price: string[], brand: string[], duration: string[], category: string[] }}
 */
function getBooxiParamsFromURL() {
  const url = new URL(window.location.href);
  /** @type {BooxiParams} */
  const params = { price: [], brand: [], duration: [], category: [] };

  for (const key of Object.keys(BOOXI_PARAMS)) {
    const param = BOOXI_PARAMS[/** @type {keyof typeof BOOXI_PARAMS} */ (key)];
    if (!param) continue;
    const values = [...new Set(
      url.searchParams.getAll(param)
        .map((value) => key === 'category' ? normalizeBooxiCategoryValue(value) : value.trim())
        .filter(Boolean)
    )];
    if (values.length) params[/** @type {keyof typeof params} */ (key)] = values;
  }

  return params;
}

/**
 * Sync Booxi filter form inputs to match current URL params.
 */
function syncFormFromURL() {
  const drawer = document.getElementById('filters-drawer');
  const wrapper = drawer?.querySelector('[data-booxi-filters]');
  if (!wrapper) return;

  const params = getBooxiParamsFromURL();
  const inputs = wrapper.querySelectorAll(BOOXI_FILTER_INPUT_SELECTOR);

  inputs.forEach((/** @type {Element} */ input) => {
    if (!(input instanceof HTMLInputElement)) return;
    const paramsKey = getBooxiParamsKeyFromInputName(input.name);
    const value = input.value?.trim();
    if (!paramsKey || !value) return;

    const values = params[paramsKey] || [];
    const normalizedValue = normalizeBooxiInputValue(value, paramsKey);
    const isChecked = values.includes(normalizedValue);
    input.checked = isChecked;
  });
}

/**
 * Toggle disabled state for Booxi facet options based on available matches.
 * Unselected options with no matching cards are disabled.
 * Selected options always stay enabled so they can be unselected.
 * @param {Document|HTMLElement} [root] - Optional root (default: document)
 */
function syncBooxiFacetDisabledState(root = document) {
  const drawer = document.getElementById('filters-drawer');
  const wrapper = drawer?.querySelector('[data-booxi-filters]');
  if (!(wrapper instanceof HTMLElement)) return;

  const params = getBooxiParamsFromURL();
  const inputs = wrapper.querySelectorAll(BOOXI_FILTER_INPUT_SELECTOR);
  const bookingCtaEnabled = wrapper.dataset.bookingCtaEnable === 'true';

  /** Price, duration, category are locked when Dermalogica brand is selected (booking CTA flow). */
  if (bookingCtaEnabled && isDermalogicaBrandActiveInParams(params)) {
    inputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const paramsKey = getBooxiParamsKeyFromInputName(input.name);
      const lockNonBrand =
        paramsKey === 'price' || paramsKey === 'duration' || paramsKey === 'category';
      const shouldDisable = lockNonBrand;

      input.disabled = shouldDisable;
      input.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');

      const checkboxWrapper = input.closest('.checkbox');
      if (checkboxWrapper instanceof HTMLElement) {
        checkboxWrapper.classList.toggle('checkbox--disabled', shouldDisable);
      }

      const inputListItem = input.closest('.facets__inputs-list-item');
      if (inputListItem instanceof HTMLElement) {
        inputListItem.classList.toggle('facets__inputs-list-item--disabled', shouldDisable);
      }
    });
    return;
  }

  const cards = Array.from((root || document).querySelectorAll('.product-grid__item--booxi-service')).filter(
    (card) => card instanceof HTMLElement
  );

  if (!cards.length) {
    inputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const paramsKey = getBooxiParamsKeyFromInputName(input.name);
      if (paramsKey !== 'price' && paramsKey !== 'duration' && paramsKey !== 'category') return;
      input.disabled = false;
      input.setAttribute('aria-disabled', 'false');
      const checkboxWrapper = input.closest('.checkbox');
      if (checkboxWrapper instanceof HTMLElement) {
        checkboxWrapper.classList.remove('checkbox--disabled');
      }
      const inputListItem = input.closest('.facets__inputs-list-item');
      if (inputListItem instanceof HTMLElement) {
        inputListItem.classList.remove('facets__inputs-list-item--disabled');
      }
    });
    return;
  }

  inputs.forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;

    let shouldDisable = false;

    if (!input.checked) {
      const hypotheticalParams = buildHypotheticalParamsForInput(params, input);
      if (!hypotheticalParams) return;
      const hasMatch = cards.some((card) => booxiCardMatchesParams(card, hypotheticalParams));
      shouldDisable = !hasMatch;
    }

    if (
      bookingCtaEnabled &&
      input.name === 'filter_service_brand' &&
      input.value.trim().toLowerCase() === 'dermalogica'
    ) {
      shouldDisable = false;
    }

    input.disabled = shouldDisable;
    input.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');

    const checkboxWrapper = input.closest('.checkbox');
    if (checkboxWrapper instanceof HTMLElement) {
      checkboxWrapper.classList.toggle('checkbox--disabled', shouldDisable);
    }

    const inputListItem = input.closest('.facets__inputs-list-item');
    if (inputListItem instanceof HTMLElement) {
      inputListItem.classList.toggle('facets__inputs-list-item--disabled', shouldDisable);
    }
  });
}

/**
 * Apply filter visibility to service cards based on URL params.
 * @param {Document|HTMLElement} [root] - Optional root (default: document)
 */
function applyFiltersFromURL(root = document) {
  const scope = root || document;
  const cards = Array.from(scope.querySelectorAll('.product-grid__item--booxi-service')).filter(
    (card) => card instanceof HTMLElement
  );

  const drawer = document.getElementById('filters-drawer');
  const booxiWrapper = drawer?.querySelector('[data-booxi-filters]');
  const bookingCtaEnabled = booxiWrapper instanceof HTMLElement && booxiWrapper.dataset.bookingCtaEnable === 'true';

  let params = getBooxiParamsFromURL();
  const { params: normalizedParams, changed: strippedNonBrandForDermalogica } = normalizeParamsWhenDermalogicaBooking(
    params,
    bookingCtaEnabled
  );
  if (strippedNonBrandForDermalogica) {
    updateURLWithBooxiParams(normalizedParams);
    params = normalizedParams;
  }

  const hasAny = params.price.length || params.brand.length || params.duration.length || params.category.length;

  syncFormFromURL();

  cards.forEach((card) => {
    if (!hasAny) {
      card.style.display = '';
      return;
    }
    const show = booxiCardMatchesParams(card, params);
    card.style.display = show ? '' : 'none';
  });

  applyBookingCtaVisibility(scope, params);

  syncBooxiFacetDisabledState(root);
  renderBooxiRemovePills();
  syncDrawerClearAllVisibility();
}

/**
 * Show/hide the facets__clear-all in the drawer based on whether any filters (standard or Booxi) are active.
 */
function syncDrawerClearAllVisibility() {
  const drawer = document.querySelector('.facets--drawer');
  if (!drawer) return;

  const clearAll = drawer.querySelector('.facets__clear-all');
  if (!clearAll) return;

  const url = new URL(window.location.href);
  const hasBooxiParams = Object.values(BOOXI_PARAMS).some((param) => url.searchParams.has(param));
  const hasStandardParams = [...url.searchParams.keys()].some((key) => key.startsWith('filter.'));
  const shouldShow = hasBooxiParams || hasStandardParams;

  clearAll.classList.toggle('active', shouldShow);
}

/**
 * Collect Booxi filter values from the drawer form.
 * @param {HTMLElement} drawer
 * @returns {{ price: string[], brand: string[], duration: string[], category: string[] }}
 */
function getBooxiParamsFromForm(drawer) {
  /** @type {BooxiParams} */
  const params = { price: [], brand: [], duration: [], category: [] };
  const wrapper = drawer?.querySelector('[data-booxi-filters]');
  if (!wrapper) return params;

  const inputs = wrapper.querySelectorAll(BOOXI_FILTER_INPUT_SELECTOR);

  inputs.forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    if (input.type === 'radio' && !input.checked) return;
    if (input.type === 'checkbox' && !input.checked) return;

    const name = input.name;
    const value = input.value?.trim();
    if (!value) return;
    const paramsKey = getBooxiParamsKeyFromInputName(name);
    if (!paramsKey) return;
    params[paramsKey].push(normalizeBooxiInputValue(value, paramsKey));
  });

  return params;
}

/**
 * Build URL without a specific Booxi param value.
 * @param {string} paramKey - One of price, brand, duration, category
 * @param {string} valueToRemove - The value to remove
 * @returns {string}
 */
function buildURLWithoutParam(paramKey, valueToRemove) {
  const url = new URL(window.location.href);
  const param = BOOXI_PARAMS[/** @type {keyof typeof BOOXI_PARAMS} */ (paramKey)];
  if (!param) return url.toString();
  const values = url.searchParams.getAll(param).filter((value) => {
    if (paramKey === 'category') {
      return normalizeBooxiCategoryValue(value) !== normalizeBooxiCategoryValue(valueToRemove);
    }
    return value.trim() !== valueToRemove;
  });
  url.searchParams.delete(param);
  values.forEach((v) => url.searchParams.append(param, v));
  return url.toString();
}

/**
 * Read the visible label for a Booxi filter input from the facets markup (locale-aware).
 * @param {string} paramKey
 * @param {string} rawValue
 * @returns {string}
 */
function getBooxiPillLabelFromDom(paramKey, rawValue) {
  const param = BOOXI_PARAMS[/** @type {keyof typeof BOOXI_PARAMS} */ (paramKey)];
  if (!param) return '';
  const root = document.querySelector('[data-booxi-filters]');
  if (!root) return '';

  const normalizedTarget =
    paramKey === 'category' ? normalizeBooxiCategoryValue(rawValue) : String(rawValue || '').trim();

  const inputs = root.querySelectorAll(`input[name="${param}"]`);
  for (const input of inputs) {
    if (!(input instanceof HTMLInputElement)) continue;
    let inputVal = input.value || '';
    if (paramKey === 'category') {
      inputVal = normalizeBooxiCategoryValue(inputVal);
    } else {
      inputVal = inputVal.trim();
    }
    if (inputVal !== normalizedTarget) continue;
    const row = input.closest('.checkbox');
    const textEl = row?.querySelector('.checkbox__label-text');
    const text = textEl?.textContent?.trim();
    if (text) return text;
  }
  return '';
}

/**
 * Get display label for a Booxi filter value.
 * @param {string} paramKey
 * @param {string} value
 * @returns {string}
 */
function getBooxiPillLabel(paramKey, value) {
  const fromDom = getBooxiPillLabelFromDom(paramKey, value);
  if (fromDom) return fromDom;

  const labels = BOOXI_LABELS[/** @type {keyof typeof BOOXI_LABELS} */ (paramKey)];
  if (labels && labels[/** @type {keyof typeof labels} */ (value)]) return labels[/** @type {keyof typeof labels} */ (value)];
  let displayValue = value.trim();
  if (paramKey === 'category') {
    displayValue = normalizeBooxiCategoryValue(value) || displayValue;
  }
  return displayValue.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Render Booxi filter remove pills in the facets-remove container.
 */
function renderBooxiRemovePills() {
  const container = document.getElementById('booxi-facets-remove');
  if (!container) return;

  const params = getBooxiParamsFromURL();
  const hasAny = params.price.length || params.brand.length || params.duration.length || params.category.length;
  if (!hasAny) {
    const iconTpl = container.querySelector('#booxi-pill-icon');
    container.innerHTML = iconTpl ? iconTpl.outerHTML : '';
    container.style.display = 'none';
    return;
  }

  const resultsUrl = container.dataset.resultsUrl || window.location.pathname;
  const iconTemplate = container.querySelector('#booxi-pill-icon');
  const iconHtml = iconTemplate ? iconTemplate.innerHTML : '<span class="svg-wrapper svg-wrapper--smaller">×</span>';

  /** @type {{ url: string, label: string, iconHtml: string }[]} */
  const pills = [];

  ['price', 'brand', 'duration', 'category'].forEach((key) => {
    const values = params[/** @type {keyof typeof params} */ (key)] || [];
    values.forEach((value) => {
      const url = buildURLWithoutParam(key, value);
      const label = getBooxiPillLabel(key, value);
      pills.push({ url, label, iconHtml });
    });
  });

  const templateHtml = iconTemplate ? iconTemplate.outerHTML : '';
  /** @param {string} s */
  const escapeHtml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  // Pills use href for accessibility; JS intercepts clicks to update without page load
  container.innerHTML = templateHtml + pills
    .map(
      ({ url, label, iconHtml }) =>
        `<a href="${escapeHtml(url)}" class="pills__pill facets-remove__pill booxi-pill-link" tabindex="0">
          ${escapeHtml(label)}
          ${iconHtml}
          <span class="visually-hidden">${escapeHtml(container.dataset.removePillLabel || 'Remove')}</span>
        </a>`
    )
    .join('');

  const clearLabel = container.dataset.clearAll || 'Clear all';
  const clearHtml = `<a href="${escapeHtml(resultsUrl)}" class="link facets__clear-all-link facets__clear-all active booxi-pill-link">${escapeHtml(clearLabel)}</a>`;
  container.insertAdjacentHTML('beforeend', clearHtml);

  container.style.display = 'flex';
}

/**
 * Update URL with Booxi params while preserving other query params.
 * @param {{ price: string[], brand: string[], duration: string[], category: string[] }} booxiParams
 */
function updateURLWithBooxiParams(booxiParams) {
  const url = new URL(window.location.href);

  Object.values(BOOXI_PARAMS).forEach((param) => url.searchParams.delete(param));

  ['price', 'brand', 'duration', 'category'].forEach((key) => {
    const k = /** @type {keyof typeof booxiParams} */ (key);
    const values = booxiParams[k] || [];
    const param = BOOXI_PARAMS[/** @type {keyof typeof BOOXI_PARAMS} */ (key)];
    if (param) {
      values.forEach((value) => {
        const normalizedValue = key === 'category' ? normalizeBooxiCategoryValue(value) : value;
        url.searchParams.append(param, normalizedValue);
      });
    }
  });

  history.pushState({}, '', url.toString());
}

/**
 * Apply Booxi filters from form and update URL/DOM.
 * @param {HTMLElement} drawer
 */
function applyBooxiFiltersFromForm(drawer) {
  let params = getBooxiParamsFromForm(drawer);
  const wrapper = drawer?.querySelector('[data-booxi-filters]');
  const bookingCtaEnabled = wrapper instanceof HTMLElement && wrapper.dataset.bookingCtaEnable === 'true';
  const { params: normalizedParams, changed } = normalizeParamsWhenDermalogicaBooking(params, bookingCtaEnabled);
  if (changed) {
    params = normalizedParams;
  }
  updateURLWithBooxiParams(params);
  applyFiltersFromURL();
}

/**
 * Initialize Booxi filters: apply from URL on load, real-time on input change.
 */
function initBooxiFilters() {
  applyFiltersFromURL();
  renderBooxiRemovePills();

  const grid = document.querySelector('.product-grid');
  if (grid) {
    const observer = new MutationObserver(() => applyFiltersFromURL());
    observer.observe(grid, { childList: true, subtree: true });
  }

  window.addEventListener('popstate', () => applyFiltersFromURL());

  // Intercept pill clicks: update URL and filters without page load
  document.addEventListener('click', (e) => {
    const link = e.target instanceof Element ? e.target.closest('#booxi-facets-remove a.booxi-pill-link') : null;
    if (link instanceof HTMLAnchorElement && link.href) {
      e.preventDefault();
      const url = link.href;
      if (url && !url.endsWith('#')) {
        history.pushState({}, '', url);
        applyFiltersFromURL();
      }
    }
  }, true);

  // Real-time: apply filters immediately when any Booxi input changes (no Apply button needed)
  document.addEventListener('change', (e) => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement)) return;
    const wrapper = input.closest('[data-booxi-filters]');
    if (!wrapper) return;

    const drawer = document.getElementById('filters-drawer');
    if (!drawer?.contains(wrapper)) return;

    const names = ['filter_service_price', 'filter_service_brand', 'filter_service_duration', 'filter_service_category'];
    if (!names.includes(input.name)) return;

    applyBooxiFiltersFromForm(drawer);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBooxiFilters);
} else {
  initBooxiFilters();
}
